import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";

const REASONS = [
  "Not using the service",
  "Privacy concerns",
  "Created by mistake",
  "Found a better alternative",
  "Session experience wasn't for me",
  "Other",
];

export default function DeleteAccountCard() {
  const { deleteAccount } = useAuth();
  const nav = useNavigate();
  // stage: idle -> reason -> confirm
  const [stage, setStage] = useState("idle");
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStage("idle"); setReason(""); setFeedback("");
  };

  const proceedFromReason = () => {
    if (!reason) { toast.error("Please pick a reason so we can improve."); return; }
    setStage("confirm");
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount({ reason, feedback: feedback.trim() || null });
      toast.success("Account deleted. Log back in within 15 days to restore it.");
      nav("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <section className="mt-16 mb-8" data-testid="delete-account-card">
      <div className="card-soft p-6 border" style={{borderColor:"rgba(230,178,58,0.35)", background:"rgba(230,178,58,0.04)"}}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-1" style={{color:"var(--accent)"}}/>
          <div className="flex-1">
            <div className="label-eyebrow" style={{color:"var(--accent)"}}>Danger zone</div>
            <div className="font-serif-display text-xl mt-1">Delete my account</div>
            <p className="text-sm mt-1" style={{color:"var(--text-secondary)"}}>
              Deletes your profile, bookings, applications and any related records. You have a 15-day grace
              period — log back in within 15 days and everything is restored automatically. Between 15-30
              days you can still recover it by emailing help.counseltaria@gmail.com. After 30 days it's
              permanently gone.
            </p>

            {stage === "idle" && (
              <button data-testid="open-delete-account" onClick={()=>setStage("reason")}
                      className="btn-outline text-sm mt-4 inline-flex items-center gap-2"
                      style={{borderColor:"var(--accent)", color:"var(--accent)"}}>
                <Trash2 className="w-4 h-4"/> Delete my account
              </button>
            )}

            {stage === "reason" && (
              <div className="mt-4 space-y-3" data-testid="delete-reason-form">
                <div>
                  <label className="label-eyebrow block mb-2">Before you go — why are you leaving?</label>
                  <select value={reason} onChange={e=>setReason(e.target.value)}
                          data-testid="delete-reason-select"
                          className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 bg-white focus:outline-none focus:border-[color:var(--accent)]">
                    <option value="">Choose a reason…</option>
                    {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Anything else you'd like us to know? <span className="lowercase opacity-70">(optional)</span></label>
                  <textarea value={feedback} onChange={e=>setFeedback(e.target.value)}
                            data-testid="delete-feedback-input"
                            rows={3}
                            placeholder="Your feedback helps us improve for the next person who reaches out."
                            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--accent)] resize-none"/>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <button data-testid="delete-reason-continue" onClick={proceedFromReason}
                          className="btn-primary text-sm inline-flex items-center gap-2"
                          style={{background:"var(--accent)"}}>
                    Continue
                  </button>
                  <button data-testid="delete-reason-cancel" type="button" onClick={reset}
                          className="btn-outline text-sm">Never mind</button>
                </div>
              </div>
            )}

            {stage === "confirm" && (
              <div className="mt-4 space-y-3" data-testid="delete-confirm-form">
                <div className="rounded-xl p-3" style={{background:"rgba(230,178,58,0.08)", border:"1px dashed rgba(230,178,58,0.4)"}}>
                  <div className="text-sm"><strong>Reason:</strong> {reason}</div>
                  {feedback && <div className="text-sm mt-1"><strong>Feedback:</strong> {feedback}</div>}
                </div>
                <p className="text-sm" style={{color:"var(--accent)"}}>
                  You'll be signed out now. Log back in within 15 days to restore everything automatically.
                  After that (up to 30 days) you can still recover it by emailing help.counseltaria@gmail.com.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button data-testid="confirm-delete-account" onClick={doDelete} disabled={busy}
                          className="btn-primary text-sm inline-flex items-center gap-2"
                          style={{background:"var(--accent)"}}>
                    <Trash2 className="w-4 h-4"/> {busy ? "Deleting…" : "Delete my account"}
                  </button>
                  <button data-testid="cancel-delete-account" type="button" onClick={reset}
                          disabled={busy}
                          className="btn-outline text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
